import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Badge, Box, ButtonBase, CircularProgress, IconButton, Popover, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {AlertCircleIcon, Message02Icon, Notification01Icon} from "@hugeicons/core-free-icons";
import moment from "moment";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {SignInContext} from "../../context/SignInContext";
import {NotificationsRepository} from "../../repositories/NotificationsRepository";
import {useStompWebsocket} from "../../hooks/useStompWebsocket";
import {subProjectLabel} from "../../utils/subProjects";

const PAGE_SIZE = 10;

// Icon tile per notification type (DESIGN.md status colours)
const TYPE_STYLES = {
  COMMENT: {icon: Message02Icon, bg: "#EEF0FE", color: "#4B55C9", label: "Comment"},
  USER_NOT_TALKING: {icon: AlertCircleIcon, bg: "#FDF4E3", color: "#A86A0C", label: "Caller not talking"},
};
const DEFAULT_STYLE = {icon: Notification01Icon, bg: "#F2F2F7", color: "#6B6B80", label: "Notification"};

// Notifications are pushed per flow only, so the menu listens to the flow of every language; message: any change
const FlowNotificationsTopic = ({flowUid, onMessage}) => {
  const [update] = useStompWebsocket(flowUid ? `/topic/notifications/${flowUid}` : null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  useEffect(() => {
    if (update) onMessageRef.current();
  }, [update]);
  return null;
};

// Bell in the registry project header: the project's notifications (every language, filtered by project id) with
// the user's unread count. Opening one marks it read and opens its conversation in supervision.
export const NotificationsMenu = ({project}) => {
  const {currentUser} = useContext(SignInContext);
  const navigate = useNavigate();
  const username = currentUser?.username;
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const requestRef = useRef(0);

  const projectId = project?.id ? String(project.id) : null;
  const isUnread = (notification) => !notification?.viewedBy?.includes(username);

  const loadCount = useCallback(() => {
    if (!projectId || !username) return;
    NotificationsRepository.getNotificationsCount({projectId, username})
      .then(response => setUnreadCount(Number(response?.data) || 0))
      .catch(error => console.error("Error loading notifications count:", error));
  }, [projectId, username]);

  const loadPage = useCallback(async (pageToLoad) => {
    if (!projectId) return;
    const requestId = ++requestRef.current;
    pageToLoad === 0 ? setLoading(true) : setLoadingMore(true);
    try {
      const response = await NotificationsRepository.getAllNotifications({projectId, page: pageToLoad, size: PAGE_SIZE});
      if (requestId !== requestRef.current) return;
      const content = response?.data?.content || [];
      setNotifications(prev => pageToLoad === 0
        ? content
        : [...prev, ...content.filter(item => !prev.some(existing => existing.id === item.id))]);
      setHasMore(!response?.data?.last);
      setPage(pageToLoad);
    } catch (error) {
      console.error("Error loading notifications:", error);
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [projectId]);

  const refresh = useCallback(() => {
    loadCount();
    loadPage(0);
  }, [loadCount, loadPage]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // url of a notification's conversation, in the sub project it belongs to (else the first one)
  const conversationUrl = (notification) => {
    const subProjects = project?.subProjects || [];
    const subProject = subProjects.find(item => String(item.id) === String(notification?.subProjectId))
      || subProjects.find(item => item.flowUid && item.flowUid === notification?.flowUid)
      || subProjects[0];
    if (!subProject || !notification?.conversationId) return null;
    return `/registry-project/${project.id}/${subProject.id}/supervision?conversation=${notification.conversationId}`;
  };

  const open = async (notification) => {
    setAnchorEl(null);
    const url = conversationUrl(notification);
    if (url) navigate(url);
    if (!isUnread(notification) || !username) return;
    try {
      await NotificationsRepository.markNotificationsAsViewed({
        notificationIds: [notification.id], username, projectId, flowUid: notification.flowUid,
      });
      setNotifications(prev => prev.map(item => item.id === notification.id
        ? {...item, viewedBy: [...(item.viewedBy || []), username]}
        : item));
      setUnreadCount(count => Math.max(0, count - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  // no ids: the backend marks every unread notification of the project, not only the loaded ones
  const markAllRead = async () => {
    if (!unreadCount || !username) return;
    try {
      await NotificationsRepository.markNotificationsAsViewed({notificationIds: [], username, projectId});
      setNotifications(prev => prev.map(item => isUnread(item) ? {...item, viewedBy: [...(item.viewedBy || []), username]} : item));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const languageName = (notification) => {
    const subProjects = project?.subProjects || [];
    if (subProjects.length < 2) return null;
    const subProject = subProjects.find(item => String(item.id) === String(notification?.subProjectId))
      || subProjects.find(item => item.flowUid && item.flowUid === notification?.flowUid);
    return subProject ? subProjectLabel(subProject) : null;
  };

  return (
    <>
      {(project?.subProjects || []).map(item => (
        <FlowNotificationsTopic key={item.id} flowUid={item.flowUid} onMessage={refresh}/>
      ))}

      <Tooltip title={"Notifications"}>
        <IconButton
          aria-label={"Notifications"}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: 32, height: 32, borderRadius: "50% !important", color: "#4A4A63",
            ...(anchorEl ? {color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)"} : {}),
            "&:hover": {bgcolor: anchorEl ? "rgba(132, 145, 247, 0.16)" : "#F4F4FA"},
          }}
        >
          <Badge
            badgeContent={unreadCount}
            max={99}
            sx={{"& .MuiBadge-badge": {
              minWidth: 16, height: 16, px: 0.5, fontSize: 10, fontWeight: 700, color: "#fff", bgcolor: "#C73A3A",
              border: "2px solid #fff", boxSizing: "content-box",
            }}}
          >
            <HugeiconsIcon icon={Notification01Icon} size={18}/>
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
        transformOrigin={{vertical: "top", horizontal: "right"}}
        slotProps={{paper: {sx: {
          width: 380, maxWidth: "calc(100vw - 24px)", mt: 0.75, borderRadius: "14px", overflow: "hidden",
          border: "1px solid #ECECF3", boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)",
        }}}}
      >
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1}
               sx={{px: 2, py: 1.5, borderBottom: "1px solid #F0F0F6"}}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>Notifications</Typography>
            {unreadCount > 0 && (
              <Box component={"span"} sx={{
                px: 1, py: 0.125, borderRadius: "999px", fontSize: 11, fontWeight: 600, color: "#4B55C9",
                bgcolor: "rgba(132, 145, 247, 0.14)",
              }}>
                {unreadCount} new
              </Box>
            )}
          </Stack>
          {unreadCount > 0 && (
            <ButtonBase onClick={markAllRead} sx={{
              px: 1, py: 0.5, borderRadius: "8px", fontSize: 12, fontWeight: 600, color: "#4B55C9",
              "&:hover": {bgcolor: "rgba(132, 145, 247, 0.1)"},
            }}>
              Mark all as read
            </ButtonBase>
          )}
        </Stack>

        <Box sx={{maxHeight: 420, overflowY: "auto"}}>
          {loading && notifications.length === 0 ? (
            <Stack alignItems={"center"} gap={1.5} sx={{py: 5}}>
              <CircularProgress size={26} thickness={5} sx={{color: "#8491F7"}}/>
              <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>Loading notifications…</Typography>
            </Stack>
          ) : notifications.length === 0 ? (
            <Stack alignItems={"center"} gap={0.75} sx={{py: 5, px: 3, textAlign: "center"}}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center",
                color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", mb: 0.5,
              }}>
                <HugeiconsIcon icon={Notification01Icon} size={20}/>
              </Box>
              <Typography sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>You're all caught up</Typography>
              <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>New notifications of this project will show up here.</Typography>
            </Stack>
          ) : (
            notifications.map(notification => {
              const style = TYPE_STYLES[notification.type] || DEFAULT_STYLE;
              const unread = isUnread(notification);
              const language = languageName(notification);
              return (
                <ButtonBase
                  key={notification.id}
                  onClick={() => open(notification)}
                  sx={{
                    width: "100%", display: "flex", alignItems: "flex-start", gap: 1.25, px: 2, py: 1.5, textAlign: "left",
                    borderBottom: "1px solid #F2F2F7", bgcolor: unread ? "#F8F8FE" : "#fff",
                    "&:hover": {bgcolor: "#F4F4FA"},
                  }}
                >
                  <Box sx={{
                    width: 34, height: 34, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center",
                    justifyContent: "center", bgcolor: style.bg, color: style.color,
                  }}>
                    <HugeiconsIcon icon={style.icon} size={17}/>
                  </Box>
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography sx={{fontSize: 13, fontWeight: unread ? 700 : 600, color: "#23264D", lineHeight: 1.35}}>
                      {notification.title || style.label}
                    </Typography>
                    {(notification.description || notification.content) && (
                      <Typography sx={{
                        mt: 0.25, fontSize: 12, color: "#6B6B80", lineHeight: 1.4,
                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                      }}>
                        {notification.description || notification.content}
                      </Typography>
                    )}
                    <Typography sx={{mt: 0.5, fontSize: 11, color: "#8A8AA0"}}>
                      {[
                        moment(notification.dateCreated).fromNow(),
                        language,
                        notification.conversationId && `#${notification.conversationId}`,
                      ].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  {unread && <Box sx={{width: 8, height: 8, mt: 0.75, borderRadius: "50%", bgcolor: "#8491F7", flexShrink: 0}}/>}
                </ButtonBase>
              );
            })
          )}
        </Box>

        {hasMore && notifications.length > 0 && (
          <Box sx={{p: 1, borderTop: "1px solid #F0F0F6"}}>
            <ButtonBase
              onClick={() => !loadingMore && loadPage(page + 1)}
              disabled={loadingMore}
              sx={{
                width: "100%", height: 34, borderRadius: "10px", fontSize: 13, fontWeight: 600, color: "#23264D",
                "&:hover": {bgcolor: "#F4F4FA"},
              }}
            >
              {loadingMore ? <CircularProgress size={14} sx={{color: "#8491F7"}}/> : "Load more"}
            </ButtonBase>
          </Box>
        )}
      </Popover>
    </>
  );
};
